import React, { useState } from "react";
import {
  useFlowController,
  Icon,
  IconColorStyle,
  IconSize,
  useFlowData,
  getFlowPathData,
  getSavedCover,
  setFlowDataDirect,
  getGuid,
  getFlowDataDirect,
  FlowTypes,
} from "@adaptis/flow-components";
import {
  addNewPet,
  loadSavedQuote,
  setPetOriginalModel,
  getPromocode,
} from "../../util/data-store";
import { PetType } from "../../util/common-types";
import LoadingLightbox from "../LoadingLightbox";
import { InputGroup, Form, FormControl } from "react-bootstrap";
import { AnimatePresence, motion } from "framer-motion";
import { ILoadQuoteProps } from "./LoadQuote.types";
import PetsyExpiredPopup from "../PetsyExpiredPopup";
import _ from "../../imports/lodash-imports";
import moment from "dayjs";
import "./LoadQuote.scss";

export default function LoadQuote(props: ILoadQuoteProps) {
  const { gotoStep } = useFlowController();
  const [showQuoteBox, setShowQuoteBox] = useState(false);
  const [quoteNo, setQuoteNo] = useState("");
  const [modalShow, setModalShow] = React.useState(false);
  const [isInvalid, setIsInvalid] = React.useState(false);
  const [modalShowIsExpired, setModalShowIsExpired] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");
  const [sessionData, setSessionData] = React.useState(null);
  const { setData } = useFlowData();

  function showInput(e: any) {
    e.preventDefault();
    setShowQuoteBox(true);
  }

  async function searchQuote(e: any) {
    e.preventDefault();
    await loadQuote();
  }

  async function onKeyUp(e: any) {
    if (e.key === "Enter") {
      await loadQuote();
    } else if (quoteNo && isValidQuoteNo(quoteNo)) {
      setIsInvalid(false);
      setErrorMessage("");
    } else if (!quoteNo) {
      setIsInvalid(false);
      setErrorMessage("");
    } else {
      setIsInvalid(true);
      setErrorMessage("Quote number is not valid");
    }
  }

  const isValidQuoteNo = (quoteNo: string): boolean => {
    // Check if quoteNo is a string
    if (typeof quoteNo !== "string") {
      return false;
    }

    // Check if starts with 'P'
    if (!quoteNo.startsWith("P")) {
      return false;
    }

    // Check length (8-20 characters)
    if (quoteNo.length < 8 || quoteNo.length > 20) {
      return false;
    }

    // Check if all characters after 'P' are numbers
    const numbersPart = quoteNo.substring(1);
    return /^\d+$/.test(numbersPart);
  };

  async function loadQuote() {
    setErrorMessage("");
    if (!isValidQuoteNo(quoteNo)) {
      setIsInvalid(true);
      setErrorMessage("Quote number is not valid");
      return;
    }
    setIsInvalid(false);
    setModalShow(true);

    var flowDataStore: any = await getFlowPathData();
    var saveCoverData: any = await getSavedCover(quoteNo);
    let sdid = quoteNo;
    let vers = "";
    if (saveCoverData) {
      let { holderAddress, pets, promoCode, promoType, parrentId } = saveCoverData;
      if (parrentId) {
        sdid = parrentId;
      }
      setFlowDataDirect("sessionId", getGuid());
      setFlowDataDirect("pets", []);
      //Set customer information
      setFlowDataDirect("holderFirstName", saveCoverData.holderFirstName);
      setFlowDataDirect("holderLastName", saveCoverData.holderLastName ?? "");
      setFlowDataDirect(
        "holderPhone",
        saveCoverData.holderPhone ? saveCoverData.holderPhone.replace("+61", "0") : "",
      );
      setFlowDataDirect("holderEmail", saveCoverData.holderEmail);
      setFlowDataDirect("holderDOB", saveCoverData.holderDOB ?? "");

      setFlowDataDirect("refs", null);

      setFlowDataDirect("holderAddress", {
        line1: holderAddress.line1,
        line2: holderAddress.line2 ?? "",
        suburb: holderAddress.suburb,
        state: holderAddress.state,
        postcode: holderAddress.postcode,
      });
      let petTypesChosen: any[] = [];
      // console.log("PPP-161 pets", pets);
      _.set(flowDataStore, "petQty", pets.length);
      _.set(flowDataStore, "isSavedQuote", true);
      _.set(flowDataStore, "isChangedData", false);
      _.set(flowDataStore, "petsyQuoteNumber", vers ? vers : sdid);
      _.set(flowDataStore, "parentQuoteNumber", sdid);
      _.set(flowDataStore, "quoteNumber", vers ? vers : sdid);
      let allPetNames: string[] = [];
      for (const [index, pet] of pets.entries()) {
        let { customCover } = pet;
        let petType = pet.petType.toLowerCase();
        if (petType == "dog") addNewPet(getFlowDataDirect("pets"), PetType.Dog);
        else addNewPet(getFlowDataDirect("pets"), PetType.Cat);
        petTypesChosen.push(petType);
        let assignedPets = await getFlowDataDirect("pets");
        allPetNames.push(pet.name);
        _.assign(assignedPets[index], {
          pet_no: pet.pet_no || index,
          name: pet.name,
          isValid: false,
          isTouched: true,
          breed: pet.breed,
          petDOB: pet.petDOB,
          startDate: pet.startDate,
          petSex: pet.petSex.toLowerCase(),
          isDesexed: pet.isDesexed,
          petAgeCategory: pet.petAgeCategory,
          petAgeOnStartDatePolicy: pet.petAgeOnStartDatePolicy,
          pet_refs: pet.pet_refs ?? {
            callReferenceId: moment().unix().toString(),
          },
          id: getGuid(),
          coverType: "custom",
          customCover: {
            quoteNumber: vers ? vers : sdid,
            parentQuoteNumber: sdid,
            paymentFrequency: "monthly",
            annualBenefitLimit: customCover.annualBenefitLimit,
            benefitPercentage: customCover.benefitPercentage,
            annualExcess: customCover.annualExcess,
            optionalExtras: customCover.optionalExtras,
            vetConsultationFees: customCover.vetConsultationFees,
            hasQuote: true,
            // premiums: customCover.premiums ?? null,
            // promo: customCover.promo ?? null,
            promoCode: customCover.promoCode ?? null,
          },
        });
      }

      setModalShow(true);
      let filteredPromoCode: any = null;
      if (promoCode) {
        filteredPromoCode = await getPromocode(promoCode);
      }

      if (filteredPromoCode) {
        let selectedPromotionCode: FlowTypes.IFlowPromotionCode = {
          promoCode: promoCode,
          promotionMessage: filteredPromoCode.promotionMessage,
          campaignName: filteredPromoCode.campaignName,
          type: filteredPromoCode.type
            ? parseInt(filteredPromoCode.type.replace("Type", ""))
            : undefined,
          imgSrc: filteredPromoCode.imagePath,
          percent: filteredPromoCode.discount,
          period: filteredPromoCode.period,
          discount: filteredPromoCode.discount,
        };
        _.set(flowDataStore, "selectedPromoCode", selectedPromotionCode);
        _.set(flowDataStore, "promoCode", promoCode);
        _.set(
          flowDataStore,
          "promoCodeType",
          promoType ? parseInt(promoType.replace("Type", "")) : "",
        );
      }
      _.set(flowDataStore, "PetTypeChosenFirst", petTypesChosen[0]);
      _.set(flowDataStore, "PetTypesChosen", petTypesChosen);
      _.set(flowDataStore, "totalAmounts.allPetNames", allPetNames.join(","));
      _.set(flowDataStore, "totalAmounts.installmentAmount", "0");
      _.set(flowDataStore, "totalAmounts.installmentBasePremium", "0");
      _.set(flowDataStore, "totalAmounts.installmentBasePremiumPlusGST", "0");
      _.set(flowDataStore, "totalAmounts.installmentStampDuty", "0");
      _.set(flowDataStore, "totalAmounts.installmentPolicyFee", "0");
      _.set(flowDataStore, "totalAmounts.installmentPolicyFeeTotal", "0");
      _.set(flowDataStore, "totalAmounts.totalInstallmentDiscountAmount", "0");

      setModalShow(false);
      let nextStep = "/custom-cover/" + getFlowDataDirect("pets[0].id");
      gotoStep(nextStep);
    } else {
      setModalShow(false);
      setErrorMessage("Quote not found");
      setIsInvalid(true);
    }
  }

  const animateProps = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.5 },
  };

  function OnClosePoupCallback() {
    setModalShowIsExpired(false);
  }

  return (
    <>
      {props.visible && (
        <div data-testid="LoadQuote" className="flc_load-quote my-2">
          <AnimatePresence>
            <motion.div key="quote-no-link" {...animateProps} className="quote-no-link">
              {!showQuoteBox && (
                <a href="" onClick={showInput}>
                  Retrieve my quote
                </a>
              )}
            </motion.div>
            {/* <motion.div key="quote-no-link" {...animateProps}>
              {!showQuoteBox && (
                <a href="" className="disabled" onClick={showInput}>
                  To retrieve a previously saved quote, please check your email inbox.
                </a>
              )}
            </motion.div> */}

            {showQuoteBox && (
              <>
                <motion.div
                  key="quote-no-input"
                  {...animateProps}
                  className="quote-no-input-container"
                >
                  <motion.div key="quote-no-input-container" className="quote-no-input-group">
                    <InputGroup>
                      <FormControl
                        // className='me-1'
                        placeholder="Enter Your Quote Number"
                        aria-label="Quote number"
                        maxLength={10}
                        value={quoteNo}
                        onChange={(e) =>
                          setQuoteNo(e.target.value ? e.target.value.toUpperCase() : "")
                        }
                        onKeyUp={onKeyUp}
                        autoFocus
                        isInvalid={isInvalid}
                      />
                      {/* {showQuoteBox && quoteNo && (
                        <a className="search-Quote" href="" onClick={searchQuote}>
                          Retrieve my quote
                        </a>
                      )} */}
                      {errorMessage && (
                        <Form.Control.Feedback type="invalid">{errorMessage}</Form.Control.Feedback>
                      )}
                    </InputGroup>
                  </motion.div>
                  <motion.div key="quote-no-input-icon" className="quote-no-input-icon">
                    <Icon
                      src="images/icons/chevron-right.svg"
                      colorStyle={IconColorStyle.PrimaryButton}
                      colorStyleHover={IconColorStyle.PrimaryButtonHover}
                      size={IconSize.Small}
                      onClick={loadQuote}
                    />
                  </motion.div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
          <LoadingLightbox show={modalShow} title="Loading your Quote..." />
        </div>
      )}
      {modalShowIsExpired && (
        <PetsyExpiredPopup
          show={modalShowIsExpired}
          sessionData={sessionData}
          onClose={OnClosePoupCallback}
        />
      )}
    </>
  );
}
